import type { Language } from '../i18n/useI18n';

export type LegalSection = {
  title: string;
  body?: string[];
  items?: string[];
};

export type LegalDocument = {
  eyebrow: string;
  title: string;
  intro: string;
  updated: string;
  sections: LegalSection[];
};

export type LegalDocumentKey = 'privacy' | 'terms' | 'deleteAccount';

export const legalDocuments: Record<LegalDocumentKey, Record<Language, LegalDocument>> = {
  "privacy": {
    "en": {
      "eyebrow": "Privacy Policy",
      "title": "DDProof Camera Privacy Policy",
      "intro": "This policy explains what DDProof Camera collects, stores, publishes and shares when you use the mobile app, proof server and public verification pages.",
      "updated": "November 5, 2026",
      "sections": [
        {
          "title": "Who we are",
          "body": [
            "DDProof Camera is provided by DD SOFT under the DDProof product name. The app creates photos with proof data that can later be checked for integrity, time evidence and server confirmation.",
            "Privacy questions can be sent to support@ddproof.com."
          ]
        },
        {
          "title": "Accounts",
          "body": [
            "Capturing photos and videos does not require an account. A verified email/password account is required to buy and recover paid reports. Firebase Authentication processes the email and credentials; RevenueCat uses the account identifier to associate purchases. The report service stores a derived account identifier and does not store your password."
          ]
        },
        {
          "title": "Data processed on your device",
          "items": [
            "Photos and proof packages that you create in the app.",
            "Project names, photo descriptions, author labels and optional metadata that you choose to record.",
            "Optional location and date/time metadata when you enable it in the app or allow the platform permission.",
            "Cryptographic keys and proof data used to sign or verify locally created proof packages.",
            "App settings, language, proof tier and local project data."
          ]
        },
        {
          "title": "Data sent to DDProof servers",
          "body": [
            "Free/local use is designed to stay local by default. Server processing is used when you choose server-confirmed proof features or open public verification surfaces."
          ],
          "items": [
            "Proof identifiers, hashes, signatures, timestamps and verification status.",
            "Technical evidence needed to verify app integrity or device/app attestation when server-confirmed proof is used.",
            "Private evidence archives for server-confirmed proof when required by the feature.",
            "Basic request metadata such as IP address, user agent, time of request and diagnostic logs needed to operate and protect the service."
          ]
        },
        {
          "title": "Public verification records",
          "body": [
            "A public verification link is intended to let another person check a proof result. It may show a proof ID, verification level, hash summaries, timestamp information, server receipt status and reason codes explaining which checks passed or failed.",
            "The public verification page is not intended to publish the original private photo, precise coordinates, private archive or raw device attestation objects by default."
          ]
        },
        {
          "title": "Subscriptions and payments",
          "body": [
            "Apple App Store or Google Play processes payment, and RevenueCat provides transaction, product, purchase and refund status. DDProof uses these identifiers to issue and restore reports. DDProof does not receive full card numbers. Other subscription products remain separate from one-time report purchases."
          ]
        },
        {
          "title": "How we use data",
          "items": [
            "Create, verify and display proof results.",
            "Associate report purchases, deliver paid results and restore access for the same account.",
            "Operate subscriptions and restore purchases.",
            "Protect the service from abuse, replay and unauthorized access.",
            "Debug, monitor and improve reliability.",
            "Comply with legal, security and platform obligations."
          ]
        },
        {
          "title": "Sharing",
          "body": [
            "We share data with service providers only as needed to operate DDProof, including Firebase/Google Cloud hosting and backend services, RevenueCat, Apple App Store and Google Play. Public proof data is shared with anyone who has the public verification link."
          ]
        },
        {
          "title": "Retention",
          "body": [
            "Personal originals and complete proof packages stay on your device by default. For a requested photo or Camera Video report, the uploaded original is deleted after processing, no later than 24 hours after upload. The issued PDF is available for download for 90 days after successful issuance. Save a local copy: the account is not an archive of originals. Encrypted recovery backups may retain deleted server PDFs for up to 14 days, and deletion-journal snapshots for up to 30 days. Deleted account access is not restored from a backup. Minimal transaction and deletion records may remain for refund handling, fraud prevention and preventing deleted accounts from being restored by backups."
          ]
        },
        {
          "title": "Your choices",
          "items": [
            "You can deny camera, photo library or location permissions, but some features may not work.",
            "You can avoid server-confirmed proof features if you want to keep proof materials local.",
            "You can delete local app data from the app or device storage.",
            "You can request review or deletion of server-side proof data by contacting support@ddproof.com with proof identifiers and enough information to verify the request."
          ]
        },
        {
          "title": "Children",
          "body": [
            "DDProof Camera is not directed to children under 13. If you believe a child provided personal data through the service, contact us so we can review the request."
          ]
        },
        {
          "title": "Changes",
          "body": [
            "We may update this policy when the product, legal requirements or platform requirements change. The updated date above shows the current version."
          ]
        }
      ]
    },
    "ru": {
      "eyebrow": "Политика конфиденциальности",
      "title": "Политика конфиденциальности DDProof Camera",
      "intro": "Этот документ объясняет, какие данные DDProof Camera обрабатывает, хранит, публикует и передаёт при использовании приложения, proof-сервера и публичных страниц проверки.",
      "updated": "5 ноября 2026",
      "sections": [
        {
          "title": "Кто мы",
          "body": [
            "DDProof Camera предоставляется DD SOFT под продуктовым названием DDProof. Приложение создаёт фотографии с доказательными данными, которые позже можно проверить на целостность, наличие временного подтверждения и серверное подтверждение.",
            "Вопросы по приватности можно отправлять на support@ddproof.com."
          ]
        },
        {
          "title": "Аккаунты",
          "body": [
            "Съёмка фото и видео не требует аккаунта. Для покупки и восстановления платных отчётов нужен аккаунт с подтверждённым email и паролем. Firebase Authentication обрабатывает email и данные входа, RevenueCat связывает покупки с идентификатором аккаунта. Сервис отчётов хранит производный идентификатор аккаунта и не хранит ваш пароль."
          ]
        },
        {
          "title": "Данные на устройстве",
          "items": [
            "Фотографии и доказательные пакеты, которые вы создаёте в приложении.",
            "Названия проектов, описания фото, подпись автора и необязательные метаданные, которые вы решаете сохранить.",
            "Опциональные данные местоположения и даты/времени, если вы включили их в приложении или выдали системное разрешение.",
            "Криптографические ключи и доказательные данные для локального подписания и проверки proof-пакетов.",
            "Настройки приложения, язык, уровень proof и локальные данные проектов."
          ]
        },
        {
          "title": "Данные, отправляемые на серверы DDProof",
          "body": [
            "Бесплатное/локальное использование спроектировано так, чтобы по умолчанию оставаться на устройстве. Серверная обработка используется, когда вы выбираете функции серверно подтверждённого proof или открываете публичные страницы проверки."
          ],
          "items": [
            "Proof ID, хэши, подписи, временные метки и статус проверки.",
            "Технические доказательства, нужные для проверки целостности приложения или device/app attestation в серверно подтверждённом режиме.",
            "Приватные evidence-архивы для серверно подтверждённого proof, если они требуются функцией.",
            "Базовые данные запроса: IP-адрес, user agent, время запроса и диагностические логи, нужные для работы и защиты сервиса."
          ]
        },
        {
          "title": "Публичные записи проверки",
          "body": [
            "Публичная ссылка проверки нужна, чтобы получатель мог проверить результат proof. На ней могут отображаться proof ID, уровень проверки, сводки хэшей, сведения о времени, статус серверной квитанции и reason codes, объясняющие пройденные и непройденные проверки.",
            "Публичная страница проверки по умолчанию не предназначена для публикации исходного приватного фото, точных координат, приватного архива или сырых объектов device attestation."
          ]
        },
        {
          "title": "Подписки и платежи",
          "body": [
            "Apple App Store или Google Play обрабатывает оплату, RevenueCat передаёт идентификаторы транзакций и товаров, статусы покупок и возвратов. DDProof использует их для выпуска и восстановления отчётов и не получает полные номера карт. Другие подписочные продукты не объединяются с разовой покупкой отчёта."
          ]
        },
        {
          "title": "Как мы используем данные",
          "items": [
            "Создание, проверка и отображение proof-результатов.",
            "Связывание покупок отчётов, доставка оплаченных результатов и восстановление доступа того же аккаунта.",
            "Работа подписок и восстановление покупок.",
            "Защита сервиса от злоупотреблений, повторного использования доказательств и несанкционированного доступа.",
            "Диагностика, мониторинг и повышение надёжности.",
            "Соблюдение юридических, безопасностных и платформенных требований."
          ]
        },
        {
          "title": "Передача данных",
          "body": [
            "Мы передаём данные поставщикам услуг только в объёме, необходимом для работы DDProof, включая Firebase/Google Cloud hosting и backend-сервисы, RevenueCat, Apple App Store и Google Play. Публичные proof-данные доступны всем, у кого есть публичная ссылка проверки."
          ]
        },
        {
          "title": "Хранение",
          "body": [
            "Личные оригиналы и полные доказательные пакеты по умолчанию остаются на устройстве. При заказе отчёта по фото или Camera Video загруженный оригинал удаляется после обработки, не позднее 24 часов после загрузки. Готовый PDF доступен для скачивания 90 дней после успешного выпуска. Сохраните локальную копию: аккаунт не является архивом оригиналов. Резервные копии могут сохранять удалённые серверные PDF до 14 дней, а снимки журнала удаления — до 30 дней. Восстановление резервной копии не возвращает доступ удалённому аккаунту. Минимальные записи о платежах и удалении могут сохраняться для обработки возвратов, предотвращения злоупотреблений и защиты от восстановления удалённых аккаунтов из резервных копий."
          ]
        },
        {
          "title": "Ваш выбор",
          "items": [
            "Вы можете не выдавать разрешения на камеру, медиатеку или геолокацию, но часть функций может не работать.",
            "Вы можете не использовать серверно подтверждённые proof-функции, если хотите хранить материалы только локально.",
            "Вы можете удалить локальные данные приложения из самого приложения или из хранилища устройства.",
            "Вы можете запросить проверку или удаление серверных proof-данных, написав на support@ddproof.com и указав proof ID и информацию, достаточную для проверки запроса."
          ]
        },
        {
          "title": "Дети",
          "body": [
            "DDProof Camera не предназначен для детей младше 13 лет. Если вы считаете, что ребёнок передал персональные данные через сервис, свяжитесь с нами для проверки запроса."
          ]
        },
        {
          "title": "Изменения",
          "body": [
            "Мы можем обновлять эту политику при изменении продукта, законодательства или требований платформ. Дата обновления выше показывает текущую версию."
          ]
        }
      ]
    },
    "kk": {
      "eyebrow": "Құпиялылық саясаты",
      "title": "DDProof Camera құпиялылық саясаты",
      "intro": "Бұл құжат DDProof Camera мобильді қолданбасын, proof серверін және ашық тексеру беттерін пайдаланғанда қандай деректер өңделетінін, сақталатынын, жарияланатынын және берілетінін түсіндіреді.",
      "updated": "2026 жылғы 5 қараша",
      "sections": [
        {
          "title": "Біз кімбіз",
          "body": [
            "DDProof Camera қолданбасын DD SOFT DDProof өнім атауымен ұсынады. Қолданба кейін тұтастық, уақыт дәлелі және серверлік растау бойынша тексеруге болатын дәлел деректері бар фотосуреттер жасайды.",
            "Құпиялылық бойынша сұрақтарды support@ddproof.com мекенжайына жіберуге болады."
          ]
        },
        {
          "title": "Аккаунттар",
          "body": [
            "Фото және видео түсіру үшін аккаунт қажет емес. Ақылы есептерді сатып алу және қалпына келтіру үшін расталған email мен құпиясөз арқылы кіретін аккаунт қажет. Firebase Authentication email мен кіру деректерін өңдейді, RevenueCat сатып алуларды аккаунт идентификаторымен байланыстырады. Есеп қызметі аккаунттың туынды идентификаторын сақтайды және құпиясөзіңізді сақтамайды."
          ]
        },
        {
          "title": "Құрылғыдағы деректер",
          "items": [
            "Қолданбада жасайтын фотосуреттер және дәлел пакеттері.",
            "Жоба атаулары, фото сипаттамалары, автор белгісі және өзіңіз сақтауды таңдаған қосымша метадеректер.",
            "Қолданбада қосқанда немесе жүйелік рұқсат бергенде сақталатын орын және күн/уақыт деректері.",
            "Жергілікті proof пакеттерін қол қою және тексеру үшін қолданылатын криптографиялық кілттер мен дәлел деректері.",
            "Қолданба баптаулары, тіл, proof деңгейі және жергілікті жоба деректері."
          ]
        },
        {
          "title": "DDProof серверлеріне жіберілетін деректер",
          "body": [
            "Тегін/жергілікті пайдалану әдепкі бойынша құрылғыда қалатындай жобаланған. Серверлік өңдеу сервермен расталған proof мүмкіндіктерін таңдағанда немесе ашық тексеру беттерін ашқанда қолданылады."
          ],
          "items": [
            "Proof ID, хэштер, қолтаңбалар, уақыт белгілері және тексеру мәртебесі.",
            "Сервермен расталған proof кезінде қолданба тұтастығын немесе device/app attestation тексеруге қажет техникалық дәлелдер.",
            "Функция қажет еткен жағдайда сервермен расталған proof үшін жеке evidence архивтері.",
            "Қызметті іске қосу және қорғау үшін қажет негізгі сұрау деректері: IP мекенжай, user agent, сұрау уақыты және диагностикалық логтар."
          ]
        },
        {
          "title": "Ашық тексеру жазбалары",
          "body": [
            "Ашық тексеру сілтемесі басқа адам proof нәтижесін тексере алуы үшін қажет. Онда proof ID, тексеру деңгейі, хэш қорытындылары, уақыт туралы мәліметтер, сервер түбіртегінің мәртебесі және өткен/өтпеген тексерулерді түсіндіретін reason codes көрсетілуі мүмкін.",
            "Ашық тексеру беті әдепкі бойынша бастапқы жеке фотосуретті, нақты координаттарды, жеке архивті немесе бастапқы device attestation объектілерін жариялауға арналмаған."
          ]
        },
        {
          "title": "Жазылымдар және төлемдер",
          "body": [
            "Төлемді Apple App Store немесе Google Play өңдейді. RevenueCat транзакция мен тауар идентификаторларын, сатып алу және ақшаны қайтару мәртебелерін береді. DDProof оларды есептерді шығару және қалпына келтіру үшін пайдаланады, толық карта нөмірлерін алмайды. Басқа жазылым өнімдері есепті бір рет сатып алудан бөлек."
          ]
        },
        {
          "title": "Деректерді қалай қолданамыз",
          "items": [
            "Proof нәтижелерін жасау, тексеру және көрсету.",
            "Есеп сатып алуларын байланыстыру, төленген нәтижелерді жеткізу және сол аккаунтқа қолжетімділікті қалпына келтіру.",
            "Жазылымдарды жүргізу және сатып алуларды қалпына келтіру.",
            "Қызметті теріс пайдалану, қайталап қолдану және рұқсатсыз кіруден қорғау.",
            "Диагностика, мониторинг және сенімділікті жақсарту.",
            "Құқықтық, қауіпсіздік және платформа талаптарын орындау."
          ]
        },
        {
          "title": "Деректерді беру",
          "body": [
            "Біз деректерді DDProof жұмыс істеуі үшін қажет көлемде ғана қызмет жеткізушілеріне береміз, оның ішінде Firebase/Google Cloud hosting және backend қызметтері, RevenueCat, Apple App Store және Google Play бар. Ашық proof деректері ашық тексеру сілтемесі бар кез келген адамға қолжетімді."
          ]
        },
        {
          "title": "Сақтау мерзімі",
          "body": [
            "Жеке түпнұсқалар мен толық дәлел пакеттері әдепкі бойынша құрылғыда қалады. Фото немесе Camera Video есебіне тапсырыс бергенде жүктелген түпнұсқа өңдеуден кейін, жүктелген сәттен бастап 24 сағаттан кешіктірілмей жойылады. Дайын PDF сәтті шығарылғаннан кейін 90 күн жүктеп алуға қолжетімді. Жергілікті көшірмені сақтаңыз: аккаунт түпнұсқалар архиві емес. Қалпына келтіру көшірмелерінде жойылған серверлік PDF файлдары 14 күнге дейін, ал жою журналының көшірмелері 30 күнге дейін сақталуы мүмкін. Резервтік көшірмеден қалпына келтіру жойылған аккаунтқа қолжетімділікті қайтармайды. Төлем және жою туралы ең аз жазбалар ақшаны қайтару, теріс пайдалануды болдырмау және жойылған аккаунттардың резервтік көшірмеден қалпына келуіне жол бермеу үшін сақталуы мүмкін."
          ]
        },
        {
          "title": "Сіздің таңдауыңыз",
          "items": [
            "Камера, медиатека немесе геолокация рұқсаттарын бермеуге болады, бірақ кейбір мүмкіндіктер жұмыс істемеуі мүмкін.",
            "Материалдарды тек жергілікті сақтағыңыз келсе, сервермен расталған proof мүмкіндіктерін қолданбауға болады.",
            "Жергілікті қолданба деректерін қолданбадан немесе құрылғы сақтауынан жоюға болады.",
            "Серверлік proof деректерін қарау немесе жою сұрауын support@ddproof.com мекенжайына proof ID және сұрауды тексеруге жеткілікті ақпаратпен жіберуге болады."
          ]
        },
        {
          "title": "Балалар",
          "body": [
            "DDProof Camera 13 жасқа толмаған балаларға арналмаған. Егер бала қызмет арқылы жеке дерек жіберді деп ойласаңыз, сұрауды қарау үшін бізге хабарласыңыз."
          ]
        },
        {
          "title": "Өзгерістер",
          "body": [
            "Өнім, заң немесе платформа талаптары өзгергенде бұл саясат жаңартылуы мүмкін. Жоғарыдағы жаңарту күні ағымдағы нұсқаны көрсетеді."
          ]
        }
      ]
    }
  },
  "terms": {
    "en": {
      "eyebrow": "Terms of Use",
      "title": "DDProof Camera Terms of Use",
      "intro": "These terms govern your use of DDProof Camera, DDProof proof services and public verification pages.",
      "updated": "November 5, 2026",
      "sections": [
        {
          "title": "Acceptance",
          "body": [
            "By installing, accessing or using DDProof Camera, you agree to these Terms of Use. If you do not agree, do not use the app or services."
          ]
        },
        {
          "title": "What DDProof provides",
          "body": [
            "DDProof Camera helps create photos with related proof data, including hashes, signatures, timestamps, proof packages, reports and optional server confirmation. DDProof provides technical verification information; it does not guarantee any legal outcome."
          ]
        },
        {
          "title": "Your responsibilities",
          "items": [
            "Use the app lawfully and only for materials you are allowed to capture, store and share.",
            "Respect privacy, consent, property, workplace and platform rules.",
            "Keep your device, photos, proof packages and public proof links secure.",
            "Review proof results and warnings before relying on them.",
            "Do not use DDProof to mislead, harass, impersonate, falsify evidence or violate rights."
          ]
        },
        {
          "title": "Proof limitations",
          "body": [
            "DDProof can help check whether a file matches recorded proof data and whether selected trust layers passed. DDProof cannot prove every fact about the photographed scene, cannot guarantee that an image was not staged, cannot replace a lawyer, expert, insurer, court or public authority, and cannot automatically make a photo legally binding."
          ]
        },
        {
          "title": "Free, Pro and server features",
          "body": [
            "Photo and Camera Video capture, available evidence collection, local originals, basic PDF/ZIP exports and verification are free. A purchase pays for a new server-signed report, not for changing the evidence or verification result. Web Capture, Screen Proof and organization services have separate terms."
          ]
        },
        {
          "title": "One-time report purchases",
          "body": [
            "One purchase covers one eligible photo or Camera Video of up to five minutes and its verified result, including supported report languages. Kazakhstan reference prices are ₸990 for a photo and ₸1,990 for a video; the store checkout shows the applicable price and availability. No subscription is required for this report. Repeat delivery during the 90-day download period does not require another purchase. After that period, a technically reproducible report for the same paid result can be reissued from your retained original without another purchase; recovery of the previous PDF bytes is not guaranteed. A failed issuance can be retried without another charge. Refunds follow the relevant store rules; a refund revokes paid report access."
          ]
        },
        {
          "title": "Subscriptions",
          "body": [
            "Subscriptions and purchases are billed by Apple App Store or Google Play under their rules. Renewal, cancellation, refunds and payment method changes are handled by the relevant store unless the store rules say otherwise. DDProof may use RevenueCat to manage entitlement status."
          ]
        },
        {
          "title": "Public links and reports",
          "body": [
            "If you share a public proof link, anyone with the link may view the public verification data. You are responsible for deciding whether a proof link, report or exported file should be shared with another person."
          ]
        },
        {
          "title": "Intellectual property",
          "body": [
            "DDProof, DDProof Camera, software, design, verification logic, trademarks and service materials belong to DD SOFT or its licensors. You keep rights to photos and content you create, subject to the rights needed for DDProof to operate the service you request."
          ]
        },
        {
          "title": "Service changes",
          "body": [
            "We may change, suspend or discontinue features when needed for security, reliability, legal compliance, platform requirements or product development."
          ]
        },
        {
          "title": "Disclaimers",
          "body": [
            "DDProof is provided on an “as is” and “as available” basis to the extent permitted by law. We do not promise uninterrupted operation, error-free verification, acceptance by any third party, or a specific legal or commercial result."
          ]
        },
        {
          "title": "Liability",
          "body": [
            "To the maximum extent permitted by law, DD SOFT is not liable for indirect, incidental, special, consequential or punitive damages, loss of data, loss of profits, loss of business, or disputes caused by your use or sharing of photos, proof packages or verification links."
          ]
        },
        {
          "title": "Contact",
          "body": [
            "Questions about these terms can be sent to support@ddproof.com."
          ]
        }
      ]
    },
    "ru": {
      "eyebrow": "Условия использования",
      "title": "Условия использования DDProof Camera",
      "intro": "Эти условия регулируют использование DDProof Camera, proof-сервисов DDProof и публичных страниц проверки.",
      "updated": "5 ноября 2026",
      "sections": [
        {
          "title": "Принятие условий",
          "body": [
            "Устанавливая, открывая или используя DDProof Camera, вы соглашаетесь с этими Условиями использования. Если вы не согласны, не используйте приложение и сервисы."
          ]
        },
        {
          "title": "Что предоставляет DDProof",
          "body": [
            "DDProof Camera помогает создавать фотографии со связанными доказательными данными: хэшами, подписями, временными метками, proof-пакетами, отчётами и необязательным серверным подтверждением. DDProof предоставляет техническую информацию для проверки; он не гарантирует юридический результат."
          ]
        },
        {
          "title": "Ваши обязанности",
          "items": [
            "Использовать приложение законно и только для материалов, которые вы вправе снимать, хранить и передавать.",
            "Соблюдать правила приватности, согласия, собственности, рабочего места и платформ.",
            "Защищать своё устройство, фотографии, proof-пакеты и публичные proof-ссылки.",
            "Проверять результаты и предупреждения перед тем, как на них полагаться.",
            "Не использовать DDProof для введения в заблуждение, преследования, выдачи себя за другого, фальсификации доказательств или нарушения прав."
          ]
        },
        {
          "title": "Ограничения proof",
          "body": [
            "DDProof помогает проверить, совпадает ли файл с записанными proof-данными и какие слои доверия пройдены. DDProof не может доказать каждый факт о снятой сцене, не гарантирует, что изображение не было постановочным, не заменяет юриста, эксперта, страховую, суд или государственный орган и не придаёт фотографии юридическую силу автоматически."
          ]
        },
        {
          "title": "Бесплатные, Pro и серверные функции",
          "body": [
            "Съёмка фото и Camera Video, сбор доступных доказательств, локальные оригиналы, базовый экспорт PDF/ZIP и проверка бесплатны. Покупка оплачивает новый серверный подписанный отчёт, а не изменение доказательств или результата проверки. Для Web Capture, Screen Proof и услуг организаций действуют отдельные условия."
          ]
        },
        {
          "title": "Разовая покупка отчёта",
          "body": [
            "Одна покупка относится к одному допустимому фото или Camera Video длительностью до пяти минут и его проверенному результату, включая поддерживаемые языки отчёта. Ориентир для Казахстана — 990 ₸ за фото и 1 990 ₸ за видео; применимую цену и доступность показывает магазин перед оплатой. Подписка для этого отчёта не нужна. Повторная доставка в течение 90 дней не требует новой покупки. После этого срока технически воспроизводимый отчёт для того же оплаченного результата можно выпустить заново из сохранённого вами оригинала без новой покупки; восстановление прежних байтов PDF не гарантируется. При сбое выпуска повторная попытка не требует повторного списания. Возвраты проходят по правилам магазина; возврат закрывает доступ к платному отчёту."
          ]
        },
        {
          "title": "Подписки",
          "body": [
            "Подписки и покупки оплачиваются через Apple App Store или Google Play по их правилам. Продление, отмена, возвраты и изменение способа оплаты обрабатываются соответствующим магазином, если правила магазина не говорят иное. DDProof может использовать RevenueCat для управления статусом доступа."
          ]
        },
        {
          "title": "Публичные ссылки и отчёты",
          "body": [
            "Если вы делитесь публичной proof-ссылкой, любой получатель с этой ссылкой может увидеть публичные данные проверки. Вы сами отвечаете за решение, стоит ли передавать другому получателю proof-ссылку, отчёт или экспортированный файл."
          ]
        },
        {
          "title": "Интеллектуальная собственность",
          "body": [
            "DDProof, DDProof Camera, программное обеспечение, дизайн, логика проверки, товарные знаки и материалы сервиса принадлежат DD SOFT или её лицензиарам. Права на созданные вами фотографии и материалы остаются у вас с учётом прав, необходимых DDProof для работы запрошенного вами сервиса."
          ]
        },
        {
          "title": "Изменения сервиса",
          "body": [
            "Мы можем менять, приостанавливать или прекращать функции, если это нужно для безопасности, надёжности, соблюдения закона, требований платформ или развития продукта."
          ]
        },
        {
          "title": "Отказ от гарантий",
          "body": [
            "DDProof предоставляется “как есть” и “по доступности” в пределах, разрешённых законом. Мы не обещаем бесперебойную работу, отсутствие ошибок проверки, принятие результата третьей стороной или конкретный юридический либо коммерческий результат."
          ]
        },
        {
          "title": "Ответственность",
          "body": [
            "В максимальной степени, разрешённой законом, DD SOFT не несёт ответственности за косвенные, случайные, специальные, последующие или штрафные убытки, потерю данных, прибыли, бизнеса или споры, вызванные использованием или передачей фотографий, proof-пакетов или ссылок проверки."
          ]
        },
        {
          "title": "Контакты",
          "body": [
            "Вопросы по условиям можно отправлять на support@ddproof.com."
          ]
        }
      ]
    },
    "kk": {
      "eyebrow": "Пайдалану шарттары",
      "title": "DDProof Camera пайдалану шарттары",
      "intro": "Бұл шарттар DDProof Camera қолданбасын, DDProof proof сервистерін және ашық тексеру беттерін пайдалануды реттейді.",
      "updated": "2026 жылғы 5 қараша",
      "sections": [
        {
          "title": "Шарттарды қабылдау",
          "body": [
            "DDProof Camera қолданбасын орнату, ашу немесе пайдалану арқылы сіз осы Пайдалану шарттарымен келісесіз. Келіспесеңіз, қолданбаны және сервистерді пайдаланбаңыз."
          ]
        },
        {
          "title": "DDProof не ұсынады",
          "body": [
            "DDProof Camera хэштер, қолтаңбалар, уақыт белгілері, proof пакеттері, есептер және қосымша серверлік растау сияқты байланысты дәлел деректері бар фотосуреттер жасауға көмектеседі. DDProof техникалық тексеру ақпаратын береді; ол құқықтық нәтижеге кепілдік бермейді."
          ]
        },
        {
          "title": "Сіздің міндеттеріңіз",
          "items": [
            "Қолданбаны заңды түрде және түсіруге, сақтауға, бөлісуге құқығыңыз бар материалдар үшін ғана пайдалану.",
            "Құпиялылық, келісім, меншік, жұмыс орны және платформа ережелерін сақтау.",
            "Құрылғыңызды, фотосуреттерді, proof пакеттерін және ашық proof сілтемелерін қорғау.",
            "Нәтижелерге сүйенбес бұрын proof нәтижелері мен ескертулерін қарау.",
            "DDProof қызметін жаңылыстыру, қудалау, басқа адамның атынан әрекет ету, дәлелді бұрмалау немесе құқықтарды бұзу үшін пайдаланбау."
          ]
        },
        {
          "title": "Proof шектеулері",
          "body": [
            "DDProof файл жазылған proof деректерімен сәйкес келе ме және қандай сенім қабаттары өтті дегенді тексеруге көмектеседі. DDProof түсірілген көрініс туралы барлық фактіні дәлелдей алмайды, сурет қойылым емес екеніне кепілдік бермейді, заңгерді, сарапшыны, сақтандырушыны, сотты немесе мемлекеттік органды алмастырмайды және фотосуретке автоматты түрде құқықтық күш бермейді."
          ]
        },
        {
          "title": "Тегін, Pro және серверлік мүмкіндіктер",
          "body": [
            "Фото және Camera Video түсіру, қолжетімді дәлелдерді жинау, жергілікті түпнұсқалар, негізгі PDF/ZIP экспорты және тексеру тегін. Сатып алу дәлелдерді немесе тексеру нәтижесін өзгертуді емес, сервер қол қойған жаңа есепті шығаруды төлейді. Web Capture, Screen Proof және ұйым қызметтерінің шарттары бөлек."
          ]
        },
        {
          "title": "Есепті бір рет сатып алу",
          "body": [
            "Бір сатып алу бір жарамды фотоға немесе ұзақтығы бес минутқа дейінгі Camera Video материалына және оның тексерілген нәтижесіне, соның ішінде қолдау көрсетілетін есеп тілдеріне қатысты. Қазақстан үшін бағдарлы баға — фотоға 990 ₸, видеоға 1 990 ₸; қолданылатын баға мен қолжетімділікті төлем алдында дүкен көрсетеді. Бұл есепке жазылым қажет емес. 90 күн ішінде қайта жүктеу үшін жаңа сатып алу қажет емес. Осы мерзімнен кейін сол төленген нәтижеге арналған есепті техникалық мүмкіндік болса, өзіңіз сақтаған түпнұсқадан қайта сатып алмай шығаруға болады; бұрынғы PDF байттарын қалпына келтіруге кепілдік берілмейді. Есеп шығару сәтсіз болса, қайталау үшін қайта төлеу қажет емес. Ақшаны қайтару дүкен ережелері бойынша жүреді; қайтарылған төлем ақылы есепке қолжетімділікті жабады."
          ]
        },
        {
          "title": "Жазылымдар",
          "body": [
            "Жазылымдар мен сатып алулар Apple App Store немесе Google Play арқылы олардың ережелері бойынша төленеді. Ұзарту, тоқтату, қайтарым және төлем әдісін өзгерту тиісті дүкен арқылы өңделеді, егер дүкен ережелері басқаша айтпаса. DDProof қолжетімділік мәртебесін басқару үшін RevenueCat қолдануы мүмкін."
          ]
        },
        {
          "title": "Ашық сілтемелер және есептер",
          "body": [
            "Ашық proof сілтемесін бөліссеңіз, сол сілтемесі бар кез келген адам ашық тексеру деректерін көре алады. Proof сілтемесін, есепті немесе экспортталған файлды басқа адаммен бөлісу керек пе, соған өзіңіз жауаптысыз."
          ]
        },
        {
          "title": "Зияткерлік меншік",
          "body": [
            "DDProof, DDProof Camera, бағдарламалық жасақтама, дизайн, тексеру логикасы, тауар белгілері және сервис материалдары DD SOFT немесе оның лицензиарларына тиесілі. Сіз жасаған фотосуреттер мен материалдарға құқықтар сізде қалады, бірақ DDProof сіз сұраған сервисті іске қосу үшін қажет құқықтарды пайдалана алады."
          ]
        },
        {
          "title": "Сервис өзгерістері",
          "body": [
            "Қауіпсіздік, сенімділік, заңға сәйкестік, платформа талаптары немесе өнімді дамыту қажет болғанда біз мүмкіндіктерді өзгертуіміз, уақытша тоқтатуымыз немесе тоқтатуымыз мүмкін."
          ]
        },
        {
          "title": "Кепілдіктерден бас тарту",
          "body": [
            "DDProof заң рұқсат ететін шекте “сол күйінде” және “қолжетімді болғанда” ұсынылады. Біз үзіліссіз жұмысқа, қатесіз тексеруге, нәтижені үшінші тараптың қабылдауына немесе нақты құқықтық не коммерциялық нәтижеге уәде бермейміз."
          ]
        },
        {
          "title": "Жауапкершілік",
          "body": [
            "Заң рұқсат ететін ең жоғары шекте DD SOFT жанама, кездейсоқ, арнайы, кейінгі немесе айыппұлдық залалдарға, деректердің, пайданың, бизнестің жоғалуына немесе фотосуреттерді, proof пакеттерін не тексеру сілтемелерін пайдалану/бөлісу салдарынан туындаған дауларға жауапты емес."
          ]
        },
        {
          "title": "Байланыс",
          "body": [
            "Осы шарттар бойынша сұрақтарды support@ddproof.com мекенжайына жіберуге болады."
          ]
        }
      ]
    }
  },
  "deleteAccount": {
    "en": {
      "eyebrow": "Account Deletion",
      "title": "How to delete DDProof Camera data",
      "intro": "You can delete the account used for report purchases and request deletion of associated server data. Local capture also works without an account.",
      "updated": "November 5, 2026",
      "sections": [
        {
          "title": "Delete your report account",
          "body": [
            "Open the report account in DDProof Camera, choose Delete account and confirm your identity by signing in again. Access closes immediately; the service removes account-linked reports and the Firebase/RevenueCat account data through its deletion workflow. Minimal transaction and deletion records remain as described in the privacy policy. Deletion does not erase files on your device, automatically refund purchases or cancel separate store subscriptions. Deleted report access cannot be recovered by creating another account. If you cannot use the app, request account deletion at support@ddproof.com; ownership must be verified before processing."
          ]
        },
        {
          "title": "Delete local data",
          "items": [
            "Delete projects, photos, proof packages or exported reports from the app where the app provides that control.",
            "Delete exported files from your device storage or photo library.",
            "Uninstall DDProof Camera to remove app-local storage controlled by the operating system."
          ]
        },
        {
          "title": "Subscriptions and store purchases",
          "body": [
            "Subscriptions are managed by Apple App Store or Google Play. Cancel or manage a subscription in the store account that was used for purchase. Restoring or deleting purchase history is controlled by the store, not by DDProof."
          ]
        },
        {
          "title": "Server-side proof records",
          "body": [
            "If you used server-confirmed proof, send a deletion request to support@ddproof.com. Include the proof IDs or public verification links you want reviewed, the platform you used, and enough information for us to verify that you are authorized to request deletion.",
            "Some records may need to be retained when deletion would affect security, fraud prevention, dispute handling, legal obligations or the integrity of a public verification record already shared with others."
          ]
        },
        {
          "title": "What to include in a request",
          "items": [
            "Your request type: delete server-side proof data, review public proof record, or privacy question.",
            "Proof IDs, public verification links or approximate creation dates.",
            "Your platform: iOS or Android.",
            "A contact email for follow-up."
          ]
        }
      ]
    },
    "ru": {
      "eyebrow": "Удаление аккаунта",
      "title": "Как удалить данные DDProof Camera",
      "intro": "Вы можете удалить аккаунт для покупки отчётов и запросить удаление связанных серверных данных. Локальная съёмка также работает без аккаунта.",
      "updated": "5 ноября 2026",
      "sections": [
        {
          "title": "Удаление аккаунта отчётов",
          "body": [
            "Откройте аккаунт отчётов в DDProof Camera, выберите удаление аккаунта и подтвердите личность повторным входом. Доступ закрывается сразу; сервис удаляет связанные отчёты и данные аккаунта Firebase/RevenueCat через процедуру удаления. Резервные копии могут сохранять удалённые серверные PDF до 14 дней, а снимки журнала удаления — до 30 дней. Восстановление резервной копии не возвращает доступ удалённому аккаунту. Минимальные записи о платежах и удалении сохраняются согласно политике конфиденциальности. Удаление не стирает файлы на устройстве, не возвращает оплату автоматически и не отменяет отдельные подписки магазина. Создание нового аккаунта не восстанавливает удалённый доступ к отчётам. Если приложение недоступно, отправьте запрос на удаление аккаунта на support@ddproof.com; перед обработкой требуется подтверждение владения аккаунтом."
          ]
        },
        {
          "title": "Удаление локальных данных",
          "items": [
            "Удалите проекты, фотографии, proof-пакеты или экспортированные отчёты из приложения, если такой контроль доступен.",
            "Удалите экспортированные файлы из хранилища устройства или медиатеки.",
            "Удалите DDProof Camera с устройства, чтобы удалить локальное хранилище приложения, управляемое операционной системой."
          ]
        },
        {
          "title": "Подписки и покупки в магазинах",
          "body": [
            "Подписки управляются Apple App Store или Google Play. Отменяйте или изменяйте подписку в аккаунте магазина, через который была сделана покупка. Восстановление или удаление истории покупок контролирует магазин, а не DDProof."
          ]
        },
        {
          "title": "Серверные proof-записи",
          "body": [
            "Если вы использовали серверно подтверждённый proof, отправьте запрос на удаление на support@ddproof.com. Укажите proof ID или публичные ссылки проверки, которые нужно рассмотреть, платформу и информацию, достаточную для подтверждения вашего права запросить удаление.",
            "Некоторые записи могут сохраняться, если удаление влияет на безопасность, предотвращение злоупотреблений, разбор споров, юридические обязанности или целостность публичной записи проверки, которой уже поделились с другими."
          ]
        },
        {
          "title": "Что указать в запросе",
          "items": [
            "Тип запроса: удалить серверные proof-данные, проверить публичную proof-запись или вопрос по приватности.",
            "Proof ID, публичные ссылки проверки или примерные даты создания.",
            "Платформа: iOS или Android.",
            "Email для ответа."
          ]
        }
      ]
    },
    "kk": {
      "eyebrow": "Аккаунтты жою",
      "title": "DDProof Camera деректерін қалай жоюға болады",
      "intro": "Есеп сатып алуға арналған аккаунтты жойып, оған байланысты серверлік деректерді жоюды сұрауға болады. Жергілікті түсіру аккаунтсыз да жұмыс істейді.",
      "updated": "2026 жылғы 5 қараша",
      "sections": [
        {
          "title": "Есеп аккаунтын жою",
          "body": [
            "DDProof Camera қолданбасында есеп аккаунтын ашып, аккаунтты жоюды таңдаңыз және қайта кіру арқылы иелікті растаңыз. Қолжетімділік бірден жабылады; қызмет байланысты есептерді және Firebase/RevenueCat аккаунт деректерін жою рәсімі арқылы өшіреді. Қалпына келтіру көшірмелерінде жойылған серверлік PDF файлдары 14 күнге дейін, ал жою журналының көшірмелері 30 күнге дейін сақталуы мүмкін. Резервтік көшірмеден қалпына келтіру жойылған аккаунтқа қолжетімділікті қайтармайды. Төлем және жою туралы ең аз жазбалар құпиялылық саясатына сай сақталады. Аккаунтты жою құрылғыдағы файлдарды өшірмейді, ақшаны автоматты түрде қайтармайды және дүкендегі бөлек жазылымдарды тоқтатпайды. Жаңа аккаунт жасау жойылған есептерге қолжетімділікті қайтармайды. Қолданбаны пайдалана алмасаңыз, support@ddproof.com мекенжайына аккаунтты жою сұрауын жіберіңіз; өңдеуден бұрын аккаунт иелігін растау қажет."
          ]
        },
        {
          "title": "Жергілікті деректерді жою",
          "items": [
            "Қолданбада мұндай басқару қолжетімді болса, жобаларды, фотосуреттерді, proof пакеттерін немесе экспортталған есептерді жойыңыз.",
            "Экспортталған файлдарды құрылғы сақтауынан немесе медиатекадан жойыңыз.",
            "Операциялық жүйе басқаратын қолданба жергілікті сақтауын жою үшін DDProof Camera қолданбасын құрылғыдан өшіріңіз."
          ]
        },
        {
          "title": "Жазылымдар және дүкен сатып алулары",
          "body": [
            "Жазылымдар Apple App Store немесе Google Play арқылы басқарылады. Жазылымды сатып алу жасалған дүкен аккаунтында тоқтатыңыз немесе басқарыңыз. Сатып алу тарихын қалпына келтіру немесе жоюды дүкен бақылайды, DDProof емес."
          ]
        },
        {
          "title": "Серверлік proof жазбалары",
          "body": [
            "Егер сервермен расталған proof қолдансаңыз, жою сұрауын support@ddproof.com мекенжайына жіберіңіз. Қаралуы керек proof ID немесе ашық тексеру сілтемелерін, платформаны және жоюды сұрауға құқығыңызды тексеруге жеткілікті ақпаратты көрсетіңіз.",
            "Кейбір жазбалар қауіпсіздікке, теріс пайдалануды болдырмауға, дауларды қарауға, заң міндеттеріне немесе басқалармен бөлісілген ашық тексеру жазбасының тұтастығына әсер етсе, сақталуы мүмкін."
          ]
        },
        {
          "title": "Сұрауда не көрсету керек",
          "items": [
            "Сұрау түрі: серверлік proof деректерін жою, ашық proof жазбасын қарау немесе құпиялылық сұрағы.",
            "Proof ID, ашық тексеру сілтемелері немесе шамамен жасалған күндер.",
            "Платформа: iOS немесе Android.",
            "Жауап алу үшін email."
          ]
        }
      ]
    }
  }
};
